import api from "services/api";
import pathnames from "routes/pathnames";

export const NOTIFICATION_REFERENCE_PARAM = "notificationRefId";

export const NOTIFICATION_PAGE_KIND = {
	OWN_LEAVE: "OWN_LEAVE",
	TEAM_LEAVE: "TEAM_LEAVE",
	CLAIM: "CLAIM",
	APPOINTMENT_UPCOMING: "APPOINTMENT_UPCOMING",
	APPOINTMENT_HISTORY: "APPOINTMENT_HISTORY",
	COURSE_ASSIGNMENT: "COURSE_ASSIGNMENT",
	COURSE_HISTORY: "COURSE_HISTORY",
	DOCUMENT: "DOCUMENT",
	TEAM_LEAVE_HISTORY: "TEAM_LEAVE_HISTORY",
	MY_OVERTIME: "MY_OVERTIME",
	MY_OVERTIME_HISTORY: "MY_OVERTIME_HISTORY",
	TEAM_OVERTIME: "TEAM_OVERTIME",
	TEAM_OVERTIME_HISTORY: "TEAM_OVERTIME_HISTORY",
};

const NOTIFICATION_TYPE_PAGE_KIND = {
	// manager is notified about a subordinate's leave
	REPORTING_MANAGER_EMPLOYEE_CREATE_LEAVE: NOTIFICATION_PAGE_KIND.TEAM_LEAVE,
	REPORTING_MANAGER_EMPLOYEE_CANCEL_LEAVE: NOTIFICATION_PAGE_KIND.TEAM_LEAVE_HISTORY,
	REPORTING_MANAGER_ADMIN_UPDATE_LEAVE_STATUS: NOTIFICATION_PAGE_KIND.TEAM_LEAVE_HISTORY,

	// employee is notified about their own leave
	EMPLOYEE_ADMIN_UPDATE_LEAVE_STATUS: NOTIFICATION_PAGE_KIND.OWN_LEAVE,
	EMPLOYEE_REPORTING_MANAGER_UPDATE_LEAVE_STATUS: NOTIFICATION_PAGE_KIND.OWN_LEAVE,

	// manager is notified about a subordinate's overtime
	REPORTING_MANAGER_EMPLOYEE_CREATE_OVERTIME_COMPENSATION: NOTIFICATION_PAGE_KIND.TEAM_OVERTIME,
	REPORTING_MANAGER_EMPLOYEE_CANCEL_OVERTIME_COMPENSATION: NOTIFICATION_PAGE_KIND.TEAM_OVERTIME_HISTORY,
	REPORTING_MANAGER_ADMIN_UPDATE_OVERTIME_COMPENSATION_STATUS: NOTIFICATION_PAGE_KIND.TEAM_OVERTIME_HISTORY,
	REPORTING_MANAGER_ADMIN_UPDATE_OVERTIME_EXPIRY_DATE: NOTIFICATION_PAGE_KIND.TEAM_OVERTIME_HISTORY,

	// employee is notified about their own overtime
	EMPLOYEE_REPORTING_MANAGER_APPROVE_OVERTIME_COMPENSATION_STATUS: NOTIFICATION_PAGE_KIND.MY_OVERTIME,
	EMPLOYEE_REPORTING_MANAGER_REJECT_OVERTIME_COMPENSATION_STATUS: NOTIFICATION_PAGE_KIND.MY_OVERTIME_HISTORY,
	EMPLOYEE_ADMIN_UPDATE_OVERTIME_COMPENSATION_STATUS: NOTIFICATION_PAGE_KIND.MY_OVERTIME_HISTORY,
	EMPLOYEE_ADMIN_UPDATE_OVERTIME_EXPIRY_DATE: NOTIFICATION_PAGE_KIND.MY_OVERTIME_HISTORY,

	UPDATE_CLAIM_STATUS: NOTIFICATION_PAGE_KIND.CLAIM,
	SCHEDULE_APPOINTMENT: NOTIFICATION_PAGE_KIND.APPOINTMENT_UPCOMING,
	UPDATE_APPOINTMENT_STATUS: NOTIFICATION_PAGE_KIND.APPOINTMENT_HISTORY,
	COURSE_REMINDER: NOTIFICATION_PAGE_KIND.COURSE_ASSIGNMENT,
	COURSE_ASSIGNED: NOTIFICATION_PAGE_KIND.COURSE_ASSIGNMENT,
	COURSE_OVERDUE: NOTIFICATION_PAGE_KIND.COURSE_HISTORY,
	COURSE_UPDATE: NOTIFICATION_PAGE_KIND.COURSE_HISTORY,
	CERTIFICATE_EXPIRING: NOTIFICATION_PAGE_KIND.COURSE_HISTORY,
	CERTIFICATE_EXPIRED: NOTIFICATION_PAGE_KIND.COURSE_HISTORY,
	UPLOAD_DOCUMENT: NOTIFICATION_PAGE_KIND.DOCUMENT,
};

const isEmptyReference = (referenceId) => referenceId === null || referenceId === undefined || referenceId === "";

const withReference = (pathname) => (referenceId) => {
	if (isEmptyReference(referenceId)) return null;

	const search = new URLSearchParams({ [NOTIFICATION_REFERENCE_PARAM]: referenceId });

	return `${pathname}?${search.toString()}`;
};

const teamLeaveHistoryRoute = async (referenceId) => {
	if (isEmptyReference(referenceId)) return null;

	const response = await api.get.leaves.managerViewDetailsList(referenceId);

	const employeeId = response?.employeeDetail?.userId;

	if (!employeeId) return null;

	const search = new URLSearchParams({ tab: "Leave History", [NOTIFICATION_REFERENCE_PARAM]: referenceId });

	return `${pathnames.leavesOverview}/${employeeId}?${search.toString()}`;
};

const NOTIFICATION_PAGE_KIND_ROUTE = {
	[NOTIFICATION_PAGE_KIND.OWN_LEAVE]: withReference(pathnames.leavesHistory),
	[NOTIFICATION_PAGE_KIND.TEAM_LEAVE]: withReference(pathnames.leaveRequests),
	[NOTIFICATION_PAGE_KIND.TEAM_LEAVE_HISTORY]: teamLeaveHistoryRoute,
	[NOTIFICATION_PAGE_KIND.CLAIM]: withReference(pathnames.claimHistory),
	[NOTIFICATION_PAGE_KIND.MY_OVERTIME]: withReference(pathnames.overtimePending),
	[NOTIFICATION_PAGE_KIND.MY_OVERTIME_HISTORY]: withReference(pathnames.overtimeHistory),
	[NOTIFICATION_PAGE_KIND.TEAM_OVERTIME]: withReference(pathnames.overtimeRequests),
	[NOTIFICATION_PAGE_KIND.TEAM_OVERTIME_HISTORY]: withReference(pathnames.overtimeTeamHistory),
	// no detail view exists for these, so they land on the relevant list page
	[NOTIFICATION_PAGE_KIND.APPOINTMENT_UPCOMING]: () => pathnames.pageUpcoming,
	[NOTIFICATION_PAGE_KIND.APPOINTMENT_HISTORY]: () => pathnames.pageAppointmentSession,
	[NOTIFICATION_PAGE_KIND.COURSE_ASSIGNMENT]: () => pathnames.ongoingAssigment,
	[NOTIFICATION_PAGE_KIND.COURSE_HISTORY]: () => pathnames.assignment,
	[NOTIFICATION_PAGE_KIND.DOCUMENT]: () => `${pathnames.profile}?tab=Document`,
};

export const hasNotificationRoute = (notificationType) => !!NOTIFICATION_TYPE_PAGE_KIND[notificationType];

const getNotificationRoute = async (notificationType, referenceId) => {
	const pageKind = NOTIFICATION_TYPE_PAGE_KIND[notificationType];

	if (!pageKind) return null;

	const routeBuilder = NOTIFICATION_PAGE_KIND_ROUTE[pageKind];

	if (!routeBuilder) return null;

	const route = await routeBuilder(referenceId);

	return route || null;
};

export default getNotificationRoute;
